import { ReactTable } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { AlertCircle, Eye, FileText, Loader2, Play, Trash2, X } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"

export default function ExtractionJobsTab() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; monitorSlug?: string; jobId?: string }
  const [processLoading, setProcessLoading] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)

  const { data, isLoading, refetch } = trpc.extraction.listExtractionJobs.useQuery({
    limit: 100,
  })
  const { mutateAsync: processJob } = trpc.extraction.processJob.useMutation()
  const { mutateAsync: rerunJob } = trpc.extraction.rerunJob.useMutation()
  const { mutateAsync: deleteJob } = trpc.extraction.deleteJob.useMutation()
  const { data: templateDetails } = trpc.extraction.getTemplate.useQuery(
    { templateId: selectedTemplate?.templateId },
    { enabled: !!selectedTemplate?.templateId },
  )

  const handleProcessJob = async (jobId: string, isRerun = false) => {
    setProcessLoading(jobId)
    try {
      if (isRerun) {
        await rerunJob({ jobId })
        toast.success("Extraction job rerun started")
      } else {
        await processJob({ jobId })
        toast.success("Extraction job processing started")
      }
      void refetch()
    } catch (error) {
      toast.error(`Failed to ${isRerun ? "rerun" : "start"} extraction job`)
      console.error(error)
    } finally {
      setProcessLoading(null)
    }
  }

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm("Are you sure you want to delete this extraction job? All extracted data will also be deleted.")) {
      return
    }

    setDeleteLoading(jobId)
    try {
      await deleteJob({ jobId })
      toast.success("Extraction job deleted")
      void refetch()
    } catch (error) {
      toast.error("Failed to delete extraction job")
      console.error(error)
    } finally {
      setDeleteLoading(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-primary">Loading extraction jobs...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Extraction Jobs</h2>
        <p className="text-sm text-primary">AI-powered extraction jobs that process your scraped data</p>
      </div>

      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <ReactTable
          data={(data || []).map((job: any) => {
            let stats = { totalResults: 0, processed: 0, extracted: 0, failed: 0, skipped: 0 }
            try {
              if (job.stats) {
                stats = { ...stats, ...JSON.parse(job.stats) }
              }
            } catch (e) {
              // Use defaults
            }

            // Parse scraping job stats to get row counts
            let scrapingStats = { itemsScraped: 0, newCount: 0, duplicates: 0 }
            try {
              if (job.scrapingJobStats) {
                scrapingStats = JSON.parse(job.scrapingJobStats)
              }
            } catch (e) {
              // Use defaults
            }

            return {
              id: job.id,
              scrapingJobId: job.scrapingJobId,
              created: new Date(job.createdAt).toLocaleString(),
              started: job.startedAt ? new Date(job.startedAt).toLocaleString() : "-",
              completed: job.completedAt ? new Date(job.completedAt).toLocaleString() : "-",
              templateId: job.templateId,
              templateVersion: job.templateVersion,
              status: job.status,
              progress: job.progress || 0,
              error: job.error,
              stats,
              scrapingJobPlatform: job.scrapingJobPlatform,
              monitorName: job.monitorName,
              monitorSlug: job.monitorSlug,
              attemptCount: job.attemptCount || 0,
              // Scraping job context
              scrapingWindowDate: job.scrapingJobWindowDate,
              scrapingWindowStartIso: job.scrapingJobWindowStartIso,
              scrapingWindowEndIso: job.scrapingJobWindowEndIso,
              scrapingJobConfig: job.scrapingJobConfig,
              scrapingJobStatus: job.scrapingJobStatus,
              scrapedRows: scrapingStats.newCount || 0,
              totalScrapedRows: scrapingStats.itemsScraped || 0,
              duplicates: scrapingStats.duplicates || 0,
            }
          })}
          columns={[
            {
              Header: "Job ID",
              accessor: "id",
              Cell: ({ row: { original } }) => (
                <div className="font-mono text-xs  max-w-[100px]">
                  <div className="truncate" title={original.id}>
                    {original.id.slice(0, 8)}...
                  </div>
                </div>
              ),
            },
            {
              Header: "Created",
              accessor: "created",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.created}</div>,
            },
            {
              Header: "Template",
              accessor: "templateId",
              Cell: ({ row: { original } }) => (
                <button
                  className="text-left text-sm hover:bg-blue-50 hover:text-blue-700 rounded p-1 transition-colors group"
                  onClick={() =>
                    setSelectedTemplate({ templateId: original.templateId, version: original.templateVersion })
                  }
                  title="View template details"
                >
                  <div className="flex items-center gap-2">
                    <div>
                      <div className=" font-medium group-hover:text-blue-700">{original.templateId}</div>
                      <div className="text-xs text-gray-500">v{original.templateVersion}</div>
                    </div>
                    <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                  </div>
                </button>
              ),
            },
            {
              Header: "Source",
              accessor: "monitorName",
              Cell: ({ row: { original } }) => (
                <div className="text-sm">
                  <div className="">{original.monitorName || "Unknown"}</div>
                  <div className="text-xs text-gray-500">{original.scrapingJobPlatform}</div>
                  <div className="text-xs text-blue-600 mt-1">{original.scrapingWindowDate || "No date"}</div>
                </div>
              ),
            },
            {
              Header: "Input Rows",
              accessor: "scrapedRows",
              Cell: ({ row: { original } }) => (
                <div className="text-sm">
                  <div className=" font-medium">
                    <span className="text-green-700">{original.scrapedRows}</span>
                    <span className="text-gray-500 ml-1">/ {original.totalScrapedRows}</span>
                  </div>
                  {original.duplicates > 0 && <div className="text-xs text-orange-600">{original.duplicates} dup</div>}
                  <div className="text-xs text-gray-500">Job: {original.scrapingJobId.slice(0, 8)}...</div>
                </div>
              ),
            },
            {
              Header: "Progress",
              accessor: "progress",
              Cell: ({ row: { original } }) => (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="">{original.progress}%</span>
                    <span className="text-xs text-gray-500">
                      {original.stats.extracted}/{original.stats.totalResults}
                    </span>
                  </div>
                  <div className="w-full bg-foregroundx rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${original.progress}%` }}
                    />
                  </div>
                  {original.stats.failed > 0 && (
                    <div className="text-xs text-destructive">{original.stats.failed} failed</div>
                  )}
                </div>
              ),
            },
            {
              Header: "Status",
              accessor: "status",
              Cell: ({ row: { original } }) => {
                const statusColors = {
                  pending: "bg-foregroundx text-gray-800",
                  queued: "bg-yellow-100 text-yellow-800",
                  running: "bg-blue-100 text-blue-800 animate-pulse",
                  completed: "bg-green-100 text-green-800",
                  failed: "bg-red-100 text-destructive",
                }
                return (
                  <div className="space-y-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusColors[original.status as keyof typeof statusColors] || "bg-foregroundx text-gray-800"
                      }`}
                    >
                      {original.status}
                    </span>
                    {original.error && (
                      <div className="group relative">
                        <AlertCircle className="w-4 h-4 text-destructive cursor-help" />
                        <div className="absolute left-0 top-full mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                          <div className="font-semibold mb-2">Error Details:</div>
                          <div className="text-xs break-words">{original.error}</div>
                        </div>
                      </div>
                    )}
                    {original.attemptCount > 1 && (
                      <div className="text-xs text-orange-600">Attempt #{original.attemptCount}</div>
                    )}
                  </div>
                )
              },
            },
            {
              Header: "Actions",
              accessor: "id",
              Cell: ({ row: { original } }) => (
                <div className="flex gap-2">
                  {/* View Extracted Data */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    onClick={() =>
                      navigate({
                        to: "/scraping",
                        // @ts-ignore
                        search: { ...search, tab: "extracted-data", jobId: original.id },
                      })
                    }
                    title="View Extracted Data"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Process Now / Rerun */}
                  {(original.status === "pending" ||
                    original.status === "failed" ||
                    original.status === "queued" ||
                    original.status === "completed" ||
                    original.status === "running") && (
                    <button
                      className="inline-flex items-center p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() =>
                        handleProcessJob(original.id, original.status === "completed" || original.status === "running")
                      }
                      disabled={processLoading === original.id}
                      title={
                        original.status === "completed"
                          ? "Rerun Extraction (will delete previous results)"
                          : original.status === "running"
                            ? "Rerun Stuck Job (will restart extraction)"
                            : "Process Now"
                      }
                    >
                      {processLoading === original.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {/* Delete */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-destructive hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleDeleteJob(original.id)}
                    disabled={deleteLoading === original.id}
                    title="Delete Job"
                  >
                    {deleteLoading === original.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ),
            },
          ]}
          enableSorting
          enablePagination
        />
      </div>

      {/* Template Details Modal */}
      {selectedTemplate && templateDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-medium ">{templateDetails.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {templateDetails.description} • v{templateDetails.version}
                </p>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-gray-400 hover:text-primary transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Template Info */}
                <div>
                  <h4 className="text-sm font-medium  mb-3">Template Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">ID:</span> {templateDetails.id}
                    </div>
                    <div>
                      <span className="text-gray-500">Version:</span> {templateDetails.version}
                    </div>
                    <div>
                      <span className="text-gray-500">Platforms:</span> {templateDetails.platforms.join(", ")}
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span
                        className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          templateDetails.enabled ? "bg-green-100 text-green-800" : "bg-foregroundx text-gray-800"
                        }`}
                      >
                        {templateDetails.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* LLM Configuration */}
                <div>
                  <h4 className="text-sm font-medium  mb-3">LLM Configuration</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Temperature:</span> {templateDetails.llm.temperature}
                    </div>
                    <div>
                      <span className="text-gray-500">Max Tokens:</span> {templateDetails.llm.maxTokens}
                    </div>
                  </div>
                </div>

                {/* System Prompt */}
                <div>
                  <h4 className="text-sm font-medium  mb-3">System Prompt</h4>
                  <div className="bg-foregroundx rounded-lg p-4">
                    <pre className="text-sm text-primary whitespace-pre-wrap font-mono">
                      {templateDetails.llm.systemPrompt}
                    </pre>
                  </div>
                </div>

                {/* User Prompt */}
                <div>
                  <h4 className="text-sm font-medium  mb-3">User Prompt Template</h4>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <pre className="text-sm text-primary whitespace-pre-wrap font-mono">
                      {templateDetails.llm.userPrompt}
                    </pre>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Variables like {"{content}"}, {"{author}"}, {"{platform}"} will be replaced with actual values
                    during extraction.
                  </p>
                </div>

                {/* Output Schema */}
                <div>
                  <h4 className="text-sm font-medium  mb-3">Expected Output Schema</h4>
                  <div className="bg-foregroundx rounded-lg p-4">
                    <pre className="text-xs text-primary whitespace-pre-wrap font-mono">
                      {JSON.stringify(templateDetails.outputSchema, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-border bg-foregroundx">
              <button
                onClick={() => setSelectedTemplate(null)}
                className="px-4 py-2 text-sm font-medium text-primary bg-background border border-gray-300 rounded-md  focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
